import * as React from "react";
import { cn } from "@gerama/utils";
import { Label } from "./label";

/**
 * shadcn's `Field` family — the mandatory form-layout primitive (skill
 * section 4/5). `FieldError` renders directly under the control and is the
 * ONLY place a field-level validation message appears; general/server
 * errors never go here (they get a banner or a toast instead).
 */
function FieldGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div data-slot="field-group" className={cn("flex flex-col gap-6", className)} {...props} />
  );
}

function Field({
  className,
  ...props
}: React.ComponentProps<"div"> & { "data-invalid"?: boolean }) {
  return <div data-slot="field" className={cn("flex flex-col gap-2", className)} {...props} />;
}

function FieldLabel({ className, ...props }: React.ComponentProps<typeof Label>) {
  return (
    <Label data-slot="field-label" className={cn("text-sm font-medium", className)} {...props} />
  );
}

function FieldDescription({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <p
      data-slot="field-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

interface FieldErrorProps extends React.ComponentProps<"p"> {
  errors?: Array<{ message?: string } | undefined | null>;
}

function FieldError({ className, errors, children, ...props }: FieldErrorProps) {
  const messages = errors?.map((e) => e?.message).filter(Boolean) as string[] | undefined;
  const content = children ?? messages?.[0];
  if (!content) return null;
  return (
    <p
      role="alert"
      data-slot="field-error"
      className={cn("text-destructive text-sm font-medium", className)}
      {...props}
    >
      {content}
    </p>
  );
}

export { Field, FieldGroup, FieldLabel, FieldDescription, FieldError };
